import styled from 'styled-components'
import { device } from '@/common/constants/device'
import classNames from 'classnames'
import LoaderHexagon from '@/components/LoaderHexagon'
import { forwardRef, useEffect, useMemo, useState } from 'react'

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`

const BtnSuccessState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(163, 208, 33, 0.1);
  padding: 10px 24px;
  border-radius: 38px;
  min-height: 36px;
  width: ${(props) => (props.block ? '100%' : 'fit-content')};
  animation: fadeIn 0.3s linear forwards;
  border: 1px solid ${({ filled }) => (filled ? 'var(--yellow)' : '#C1C1C1')};
  @media ${device.laptopL} {
    padding: 14px 24px;
  }
`

const NotificationIcon = styled.div`
  width: 24px;
  height: 24px;
  background: ${({ icon }) =>
    `url(/_next_static/icons/${icon}.svg) no-repeat center/contain`};
`

const Btn = styled.button.attrs(({ className, buttonType = '' }) => ({
  className: `${className ?? ''} ${buttonType}`,
}))`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  padding: 12px 24px;

  border: 1px solid ${({ filled }) => (filled ? 'var(--yellow)' : '#C1C1C1')};
  border-radius: 38px;

  font-weight: 600;
  font-size: ${({ size }) => (size ? `${size}px` : '14px')};
  line-height: 140%;

  min-height: 36px;
  width: ${(props) => (props.block ? '100%' : 'fit-content')};
  min-width: ${(props) => (props.minWidth ? `${props.minWidth}px` : 'initial')};
  pointer-events: ${({ isLoading }) => (isLoading ? 'none' : 'initial')};

  color: var(--black);
  background-color: ${(props) =>
    props.filled ? 'var(--yellow)' : 'transparent'};

  @media ${device.tablet} {
    font-size: ${({ sizeT }) => sizeT && `${sizeT}px`};
  }

  @media ${device.laptopL} {
    padding: 16px 24px;
    font-size: ${({ sizeL }) => sizeL && `${sizeL}px`};
  }

  &.fb {
    color: var(--blue-dark);
    border-color: var(--blue-dark);
    justify-content: center;
    gap: 10px;

    &:hover {
      background-color: var(--blue-dark);
      border-color: var(--blue-dark);
      color: var(--white);
    }

    &:active {
      background-color: #4e6c8e;
      border-color: #4e6c8e;
    }
  }

  &.google {
    color: var(--blue-dark);
    background-color: var(--white);
    border-color: var(--blue-dark);
    justify-content: center;
    gap: 10px;

    &:active {
      background-color: rgb(232, 240, 254);
      border-color: #4e6c8e;
    }
  }

  &:hover {
    background-color: ${(props) => (props.filled ? '#FAE21A' : 'transparent')};
    border-color: ${(props) => (props.filled ? '#FFDA58' : '#3F3F49')};
  }

  &:active {
    background-color: ${(props) => (props.filled ? '#EACB13' : '#EDEDEE')};
    border-color: ${(props) => (props.filled ? '#F4D48A' : '#EDEDEE')};
  }

  &:disabled {
    color: #c4c4c4;
    border-color: #c4c4c4;
    background-color: ${(props) => (props.filled ? '#ECECEC' : 'transparent')};
    pointer-events: none;
    opacity: ${(props) => (props.filled ? '1' : '0.7')};
  }

  &.dark {
    border: 4px solid transparent;
    background-color: ${(props) => (props.filled ? '#E0E1E2' : 'transparent')};
    transition: background-color 0.26s ease-in-out,
      border-color 0.26s ease-in-out;

    &:hover {
      background-color: #3f3f49;
    }

    &:active {
      background-color: #15151f;
    }

    &:focus {
      border-color: rgba(97, 71, 255, 0.2);
      background-color: transparent;
    }
  }

  &.purple {
    color: var(--white);
    border: 4px solid transparent;
    background-color: ${(props) =>
      props.filled ? `var(--purple)` : 'transparent'};
    transition: background-color 0.26s ease-in-out;

    &:hover {
      background-color: #4c49c9;
    }

    &:active,
    &:focus {
      background-color: #3d3b94;
    }
  }

  &.red {
    color: var(--white);
    border: 4px solid transparent;
    background-color: ${(props) =>
      props.filled ? `var(--danger)` : 'transparent'};
    transition: background-color 0.26s ease-in-out;

    &:hover {
      background-color: #fd5d4e;
    }

    &:active,
    &:focus {
      background-color: #b24137;
    }

    &:disabled {
      color: #c4c4c4;
      background-color: ${(props) =>
        props.filled ? '#ECECEC' : 'transparent'};
      pointer-events: none;
      opacity: ${(props) => (props.filled ? '1' : '0.7')};
    }
  }

  &.loading {
    .button-content {
      opacity: 0;
      pointer-events: none;
    }
  }
`
const Loading = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: inline-block;
`

const Button = forwardRef(
  (
    {
      children,
      isLoading,
      isShowSuccessState,
      className = '',
      filled,
      ...props
    },
    ref
  ) => {
    const [showSuccess, setShowSuccess] = useState(false)
    const ButtonClasses = classNames({
      loading: isLoading,
    })

    const loaderColor = useMemo(() => {
      return filled ? 'white' : 'yellow'
    }, [filled])

    useEffect(() => {
      if (isShowSuccessState) {
        setShowSuccess(true)
        const successTimeout = setTimeout(() => {
          setShowSuccess(false)
        }, 1000)

        return () => clearTimeout(successTimeout)
      }
    }, [isShowSuccessState])

    return (
      <>
        {showSuccess ? (
          <BtnSuccessState
            {...props}
            className={`btn-success ${ButtonClasses} ${className}`}
          >
            <NotificationIcon icon="check-success" size="18" />
          </BtnSuccessState>
        ) : (
          <Btn
            className={`${ButtonClasses} ${className}`}
            isLoading={isLoading}
            ref={ref}
            filled={filled}
            {...props}
          >
            {isLoading && (
              <Loading data-testid="button-loader">
                <LoaderHexagon
                  {...props}
                  width={20}
                  height={11.5}
                  small
                  color={loaderColor}
                />
              </Loading>
            )}
            <ButtonContent className="button-content">{children}</ButtonContent>
          </Btn>
        )}
      </>
    )
  }
)

export default Button
